import React, { FC, useEffect, useState } from 'react';
import { Box, Button, IconButton, Paper, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Header } from '../../components/header';
import SearchBar from '../../components/search-bar';
import { Help, Search, Time, User } from '../../components/header/components';
import DeviceLabel from '../../components/device-label';
import { AppState } from '../../store';
import useStyles from './styles';
import DeviceDetails from '../../components/device-details';
import { setSelectedEntityFromDeviceOrTelematicsId } from '../../store/search/actions';
import { clearDeviceState, getDeviceByKey, setDeviceRadio } from '../../store/devices/actions';
import AcceptIcon from '../../assets/icons/accept-icon';
import CancelIcon from '../../assets/icons/cancel-icon';
import { updateDevice } from './calls';
import { enqueueSnackbarAction } from '../../store/snackbar/actions';

const DeviceDetailSearch: FC = () => {
  const dispatch = useDispatch();
  const { deviceId } = useParams<any>();
  const selectedEntity: any = useSelector((state: AppState) => state.search.selectedEntity);
  const selectedDevice: any = useSelector((state: AppState) => state.devices.selectedDevice);
  const login = useSelector((state: AppState) => state.login);
  const [isSave, setIsSave] = useState(false);
  const [details, setDetails] = useState(selectedDevice);
  const [patchDetails, setPatchDetails] = useState<{ [key: string]: any }>({});
  const classes = useStyles();

  useEffect(() => {
    if (!selectedEntity && !selectedEntity?.deviceKey && deviceId) {
      dispatch(setSelectedEntityFromDeviceOrTelematicsId(deviceId));
    } else if (selectedEntity?.deviceKey)
      dispatch(getDeviceByKey({ deviceKey: selectedEntity.deviceKey }));
  }, [selectedEntity, deviceId]);

  useEffect(() => {
    if (!selectedEntity?.deviceKey) dispatch(clearDeviceState());
    setDetails(selectedDevice);
  }, [selectedDevice, selectedEntity]);

  const clear = () => {
    setIsSave(false);
    setPatchDetails({});
  };

  const accept = async () => {
    try {
      const device = await updateDevice({ login, selectedDevice, patchDetails });
      await dispatch(setDeviceRadio(device));
      dispatch(
        enqueueSnackbarAction({
          message: 'Device updated successfully',
          options: {
            variant: 'success',
          },
        }),
      );
      clear();
    } catch (error) {
      dispatch(
        enqueueSnackbarAction({
          message: 'Error in updating the device, please try again',
          options: {
            variant: 'error',
          },
        }),
      );
    }
  };

  const cancel = () => {
    setIsSave(false);
    setDetails({ ...selectedDevice });
    setPatchDetails({});
  };

  const save = () => {
    setIsSave(true);
  };

  const handleChange = (section: string, path: string, value: string) => {
    const key: string = path.split('.').pop() || '';
    setPatchDetails({
      ...patchDetails,
      [section]: {
        ...patchDetails[section],
        [key]: value,
      },
    });
  };

  return (
    <>
      {!selectedEntity ? (
        <>
          <Header title="Device Details"
            headerComponents={[<Help userLink='/SitePages/Search-Page-Manual.aspx' />, <Time suppressPopover />, <User />]}
          />
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="calc(100vh - 5vh - 64px)"
            paddingBottom="5vh"
          >
            <SearchBar page="/device-details" />
          </Box>
        </>
      ) : (
        <>
          <Header
            title="Device Details"
            headerComponents={
              selectedEntity
                ? [<Help userLink='/SitePages/Device-Detail-Page.aspx?promotedState=0'/>, <Search page="device-details" />, <Time suppressPopover />, <User />]
                : [<User />]
            }
          />
          <Box className={classes.scrollBox}>
            <Box className={classes.margin} display="flex" flexDirection="row" alignItems="top">
              <DeviceLabel />
            </Box>
            <Box mx={3} mb={3}>
              <Paper>
                <DeviceDetails onChange={handleChange} details={details} />
                {Object.keys(patchDetails).length > 0 && (
                  <Box
                    px={4}
                    pb={4}
                    display="flex"
                    justifyContent="flex-end"
                    flexDirection="column"
                  >
                    <Box display="flex" justifyContent="flex-end">
                      <Button
                        className={classes.button}
                        variant="outlined"
                        size="small"
                        onClick={cancel}
                      >
                        Cancel
                      </Button>
                      <Button
                        className={classes.button}
                        variant="outlined"
                        size="small"
                        onClick={save}
                      >
                        Save
                      </Button>
                    </Box>
                    {isSave && (
                      <Box
                        flex={1}
                        className={classes.confirm}
                        display="flex"
                        justifyContent="flex-end"
                      >
                        <Typography>
                          {`Do you want to save changes to ${selectedEntity.deviceId}-${selectedEntity.moduleType}-
                          ${selectedEntity.assetSerialNumber}?`}
                        </Typography>
                        <IconButton
                          id="config-files-confirm"
                          onClick={accept}
                          aria-label="accept"
                          size="large"
                        >
                          <AcceptIcon />
                        </IconButton>
                        <IconButton
                          id="config-files-deny"
                          onClick={clear}
                          aria-label="cancel"
                          size="large"
                        >
                          <CancelIcon />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                )}
              </Paper>
            </Box>
          </Box>
        </>
      )}
    </>
  );
};

export default DeviceDetailSearch;
